<template>
  <div class="space-y-1.5">
    <form-select class="w-full text-sm" v-model="taskType">
      <option v-for="t in taskTypes" :key="t.value" :value="t.value">{{ t.label }}</option>
    </form-select>

    <form-select
      v-if="taskType === 'play_media'"
      class="w-full text-sm"
      :modelValue="mediaPlayerService"
      @update:modelValue="setMediaPlayerService">
      <option v-for="option in MEDIA_PLAYER_SERVICES" :key="option.value" :value="option.value">
        {{ option.label }}
      </option>
    </form-select>
    <form-select v-else-if="taskType === 'media_player'" class="w-full text-sm" disabled>
      <option>Text to speech</option>
    </form-select>

    <!-- Toggle / Turn On / Turn Off — pick the provider domain only -->
    <form-select
      v-else-if="['toggle', 'turn_on', 'turn_off'].includes(taskType)"
      class="w-full text-sm" v-model="provider">
      <option value="">— provider —</option>
      <option v-for="p in supportedActions" :key="p" :value="p">{{ p }}</option>
    </form-select>

    <!-- Delay — seconds only -->
    <div v-else-if="taskType === 'delay'" class="flex items-center gap-1.5">
      <form-label class="text-xs text-gray-400 whitespace-nowrap">Seconds</form-label>
      <form-input type="number" min="0" step="0.1" class="flex-1 text-sm"
        :modelValue="delaySeconds" @update:modelValue="setDelaySeconds" />
    </div>

    <!-- Repeat — wraps a service call, duplicated N times at edit time -->
    <div v-else-if="taskType === 'repeat'" class="space-y-1.5 bg-slate-800/40 rounded p-2">
      <div class="flex items-center gap-1.5">
        <form-label class="text-xs text-gray-400 whitespace-nowrap">Repeat</form-label>
        <form-input type="number" min="1" step="1" class="w-16 text-sm"
          :modelValue="repeatCount" @update:modelValue="setRepeatCount" />
        <form-label class="text-xs text-gray-400 whitespace-nowrap">times</form-label>
      </div>
      <form-select class="w-full text-sm" v-model="provider">
        <option value="">— provider —</option>
        <option v-for="p in supportedActions" :key="p" :value="p">{{ p }}</option>
      </form-select>
      <form-input type="text" placeholder="service (e.g. toggle)" class="w-full text-sm"
        :modelValue="service" @update:modelValue="setService" />
    </div>

    <!-- Call Service (Advanced) — HA's registered action catalog; target and
         data remain in the row's own columns. -->
    <div v-else class="space-y-1.5">
      <button
        type="button"
        class="ha-builder-control inline-flex w-full items-center justify-between gap-2 text-left"
        aria-haspopup="dialog"
        :aria-expanded="servicePickerOpen"
        @click="servicePickerOpen = true">
        <span class="min-w-0 truncate font-mono text-sm" :class="service ? 'text-slate-100' : 'text-slate-400'">
          {{ service || 'Select service/action…' }}
        </span>
        <svg class="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="11" cy="11" r="6.5" stroke-width="1.8" />
          <path d="m16 16 4 4" stroke-width="1.8" stroke-linecap="round" />
        </svg>
      </button>
      <p v-if="serviceCatalogueError" class="flex items-center justify-between gap-2 text-xs text-amber-400">
        <span>{{ serviceCatalogueError }}</span>
        <button type="button" class="shrink-0 text-cyan-400 hover:text-cyan-300" @click="emit('retry-services')">Retry</button>
      </p>
      <div class="flex items-center gap-1 text-xs" role="tablist" aria-label="Service action scope">
        <button
          type="button"
          class="rounded px-2 py-1"
          :class="servicePickerMode === 'recommended' ? 'bg-cyan-900/50 text-cyan-200' : 'text-slate-400 hover:text-slate-200'"
          @click="servicePickerMode = 'recommended'">
          Recommended ({{ recommendedServiceActions.length }})
        </button>
        <button
          type="button"
          class="rounded px-2 py-1"
          :class="servicePickerMode === 'all' ? 'bg-cyan-900/50 text-cyan-200' : 'text-slate-400 hover:text-slate-200'"
          @click="servicePickerMode = 'all'">
          All actions ({{ allServiceActions.length }})
        </button>
      </div>
      <SearchablePicker
        v-model:show="servicePickerOpen"
        title="Select Home Assistant service/action"
        search-label="Search Home Assistant actions"
        placeholder="Search domain, action, name, or description…"
        :items="targetServiceActions"
        :selected-value="service"
        unknown-value-label="action"
        :loading="serviceCatalogueLoading"
        :unavailable-message="serviceCatalogueError"
        @select="setAdvancedService" />
    </div>
  </div>
</template>

<script setup>
/**
 * ActionEditor.vue
 *
 * Task-oriented action picker over a rule's `model.actions[]`. Toggle/Turn
 * On/Turn Off/Delay/Call Service map 1:1 onto a single AutomationAction;
 * Repeat is UI sugar — it duplicates one call_service action `count` times
 * into `model.actions` at edit time (no AST or generator change needed, since
 * HomeAssistantGenerator already renders each action in the array in order).
 *
 * The `entity_id` parameter is intentionally never edited here — the parent
 * row's "Target" column (always the Home Assistant Entity Picker) owns it
 * and keeps it in sync across every action in the array.
 */
import { computed, ref, watch } from 'vue';
import { AutomationAction } from '../../../automation/ast/AutomationAction.js';
import { AutomationValue } from '../../../automation/ast/AutomationValue.js';
import {
  normalizeAdvancedCallService,
  setAdvancedCallService,
} from '../../../automation/callServiceAction.js';
import { getParamValue, setParam } from './automationParams.js';
import {
  parseTtsAction,
  DEFAULT_TTS_LANGUAGE,
  DEFAULT_TTS_VOICE,
  DEFAULT_TTS_PROVIDER,
} from '../../../automation/mediaPlayerTtsAction.js';
import {
  parseMediaPlaybackAction,
  DEFAULT_MEDIA_CONTENT_TYPE,
  MEDIA_PLAYER_SERVICES,
  defaultMediaPlayerData,
  isMediaPlayerService,
} from '../../../automation/mediaPlayerAction.js';
import { createTemplateDocument, createTextNode } from '../../../automation/templateDocument.js';
import { getPreferredTtsDefaults } from '../../../services/homeAssistantService.js';
import SearchablePicker from '../../SearchablePicker.vue';
import { switchServiceFields } from './serviceFields.js';

const props = defineProps({
  model:            { type: Object, required: true }, // AutomationModel — mutates model.actions directly
  settings:         { type: Object, default: null },
  supportedActions: { type: Array,  default: () => [] },
  serviceActions:   { type: Array,  default: () => [] },
  recommendedActionDomains: { type: Array, default: () => [] },
  serviceCatalogueLoading: { type: Boolean, default: false },
  serviceCatalogueError: { type: String, default: '' },
  // One action per rule: a Home Assistant flow node holds exactly one action, so no Repeat.
  singleAction: { type: Boolean, default: false },
});
const emit = defineEmits(['retry-services']);

const TASK_TYPES = [
  { value: 'toggle',   label: 'Toggle' },
  { value: 'turn_on',  label: 'Turn On' },
  { value: 'turn_off', label: 'Turn Off' },
  { value: 'activate_scene', label: 'Activate Scene' },
  { value: 'media_player', label: 'Text to speech' },
  { value: 'play_media', label: 'Media player' },
  { value: 'delay',    label: 'Delay' },
  { value: 'repeat',   label: 'Repeat' },
  { value: 'advanced', label: 'Action (Advanced)' },
];
const taskTypes = computed(() => (props.singleAction ? TASK_TYPES.filter(type => type.value !== 'repeat') : TASK_TYPES));

function detectTaskType(actions) {
  if (actions.length > 1) return 'repeat';
  const a = actions[0];
  if (!a) return 'advanced';
  // A text to speech action is one before its media player entity is chosen too.
  if (parseTtsAction(a) || a._editorMode === 'media_player' || a.editorMetadata?.operation === 'tts') return 'media_player';
  if (parseMediaPlaybackAction(a) || (a.provider === 'media_player' && isMediaPlayerService(a.service))) return 'play_media';
  if (a.provider === 'scene' && a.service === 'turn_on') return 'activate_scene';
  if (a.type === 'delay') return 'delay';
  if (a.type === 'call_service' && ['toggle', 'turn_on', 'turn_off'].includes(a.service)) return a.service;
  return 'advanced';
}

function isValidPlaybackDraft(draft) {
  return Boolean(
    draft?.entityId?.startsWith('media_player.') &&
    draft?.mediaContentId?.trim() &&
    draft?.mediaContentType?.trim()
  );
}

const taskType = ref(detectTaskType(props.model.actions));
const servicePickerOpen = ref(false);
const servicePickerMode = ref(props.recommendedActionDomains.length > 0 ? 'recommended' : 'all');
if (taskType.value === 'advanced') {
  props.model.actions.forEach(normalizeAdvancedCallService);
}

const firstAction = computed(() => props.model.actions[0]);
const allServiceActions = computed(() => props.serviceActions.map(option => {
  const recommended = option.recommended === true || props.recommendedActionDomains.includes(option.domain);
  if (option.recommended === recommended) return option;
  Object.defineProperty(option, 'recommended', { value: recommended, enumerable: false, configurable: true });
  return option;
}));
const recommendedServiceActions = computed(() => allServiceActions.value.filter(option => option.recommended));
const targetServiceActions = computed(() => {
  const visible = servicePickerMode.value === 'all' ? allServiceActions.value : recommendedServiceActions.value;
  const current = allServiceActions.value.find(option => option.value === service.value);
  if (current && !visible.some(option => option.value === current.value)) return [current, ...visible];
  return visible;
});

/** Preserve the action payload across a task-type switch. */
function carryActionPayload(action) {
  const oldParameters = action?.parameters;
  const eid = oldParameters?.find(p => p.key === 'entity_id');
  const isPlayback = action?.provider === 'media_player' && action?.service === 'play_media';
  return {
    parameters: eid ? [{ key: 'entity_id', ...(eid.source != null ? { source: eid.source } : {}), value: eid.value }] : [],
    target: action?.target ?? null,
    data: action?.editorMetadata?.operation === 'tts' || isPlayback ? null : action?.data ?? null,
  };
}

function rebuildForTaskType(type) {
  if (type === 'media_player') {
    const action = props.model.actions[0];
    const parsed = parseTtsAction(action);
    const preferred = getPreferredTtsDefaults();
    action._editorMode = 'media_player';
    action._mediaDraft = parsed ?? {
      entityId: '',
      operation: 'tts',
      message: createTemplateDocument([createTextNode('')]),
      provider: preferred?.provider ?? DEFAULT_TTS_PROVIDER,
      language: preferred?.language ?? DEFAULT_TTS_LANGUAGE,
      voice: preferred?.voice ?? DEFAULT_TTS_VOICE,
      isNew: true,
    };
    action._validationError = true;
    return;
  }

  if (type === 'play_media') {
    const action = props.model.actions[0];
    const parsed = parseMediaPlaybackAction(action);
    action.provider = 'media_player';
    action.service = 'play_media';
    action._editorMode = 'media_playback';
    action._mediaDraft = parsed ?? {
      entityId: '',
      sourceType: 'media_source',
      mediaContentId: '',
      mediaContentType: DEFAULT_MEDIA_CONTENT_TYPE,
      announce: null,
      enqueue: '',
      isNew: true,
    };
    action._validationError = !isValidPlaybackDraft(action._mediaDraft);
    return;
  }

  if (type === 'advanced') {
    for (const action of props.model.actions) {
      normalizeAdvancedCallService(action);
      delete action._editorMode;
      delete action._mediaDraft;
      delete action._validationError;
    }
    return;
  }

  const carried = carryActionPayload(props.model.actions[0]);

  if (type === 'delay') {
    props.model.actions = [new AutomationAction({
      type: 'delay',
      parameters: [{ key: 'seconds', value: AutomationValue.literal('1') }],
    })];
    return;
  }

  // toggle/turn_on/turn_off preset the service name; repeat/advanced start blank.
  const presetService = ['toggle', 'turn_on', 'turn_off'].includes(type) ? type : '';
  props.model.actions = [new AutomationAction({
    type: 'call_service',
    provider: type === 'activate_scene' ? 'scene' : '',
    service: type === 'activate_scene' ? 'turn_on' : presetService,
    ...carried,
    ...(type === 'activate_scene' ? {
      target: carried.target?.entity_id != null ? { entity_id: carried.target.entity_id } : null,
    } : {}),
  })];
}

// watch() without { immediate: true } never fires at setup — only on an
// actual change — so this rebuilds exactly once per user-driven dropdown
// change, never on initial mount.
watch(taskType, (type) => rebuildForTaskType(type));

const initialTts = parseTtsAction(props.model.actions[0]);
if (initialTts) {
  props.model.actions[0]._editorMode = 'media_player';
  props.model.actions[0]._mediaDraft = initialTts;
  props.model.actions[0]._validationError = false;
}
const initialPlayback = parseMediaPlaybackAction(props.model.actions[0]);
if (initialPlayback) {
  props.model.actions[0]._editorMode = 'media_playback';
  props.model.actions[0]._mediaDraft = initialPlayback;
  props.model.actions[0]._validationError = !isValidPlaybackDraft(initialPlayback);
}

// ── Provider / service (shared by toggle/turn_on/turn_off/repeat/advanced) ──

// 'repeat' duplicates the same provider/service across every flattened
// action, so both setters always propagate to the whole actions array.
const provider = computed({
  get: () => firstAction.value?.provider ?? '',
  set: (v) => { for (const a of props.model.actions) a.provider = v; },
});

const service = computed({
  get: () => firstAction.value?.service ?? '',
  set: (v) => { for (const a of props.model.actions) a.service = v; },
});
function setService(v) { service.value = v; }
function setProvider(v) { provider.value = v; }
function setAdvancedService(v) {
  const nextOption = allServiceActions.value.find(option => option.value === v) ?? null;
  props.model.actions.forEach(action => {
    const previousId = normalizeAdvancedCallService(action);
    const previousOption = allServiceActions.value.find(option => option.value === previousId) ?? null;
    switchServiceFields(action, previousOption, nextOption);
    setAdvancedCallService(action, v, nextOption);
  });
}

const mediaPlayerService = computed({
  get: () => isMediaPlayerService(service.value) ? service.value : 'play_media',
  set: setMediaPlayerService,
});

function setMediaPlayerService(serviceName) {
  const action = props.model.actions[0];
  if (!action || !isMediaPlayerService(serviceName)) return;

  const previousPlayback = parseMediaPlaybackAction(action) ?? action._mediaDraft;
  const previousParameters = action.parameters;
  action.provider = 'media_player';
  action.service = serviceName;
  action.parameters = action.parameters.filter(parameter => parameter.key === 'entity_id');

  if (serviceName === 'play_media') {
    action.data = null;
    action.editorMetadata = null;
    action._editorMode = 'media_playback';
    action._mediaDraft = previousPlayback ?? {
      entityId: action.target?.entity_id ?? '',
      sourceType: 'media_source',
      mediaContentId: '',
      mediaContentType: DEFAULT_MEDIA_CONTENT_TYPE,
      announce: null,
      enqueue: '',
      isNew: true,
    };
    action._validationError = !isValidPlaybackDraft(action._mediaDraft);
    return;
  }

  delete action._editorMode;
  delete action._mediaDraft;
  delete action._validationError;
  action.editorMetadata = null;
  action.data = defaultMediaPlayerData(serviceName, action.data ?? {}, previousParameters) ?? null;
}

// ── Delay ────────────────────────────────────────────────────────────────

const delaySeconds = computed(() => getParamValue(firstAction.value?.parameters ?? [], 'seconds', '1'));
function setDelaySeconds(v) {
  if (firstAction.value) setParam(firstAction.value.parameters, 'seconds', v);
}

// ── Repeat ───────────────────────────────────────────────────────────────

const repeatCount = ref(Math.max(props.model.actions.length, 2));
function setRepeatCount(v) {
  const count = Math.max(1, parseInt(v, 10) || 1);
  repeatCount.value = count;
  applyRepeatFlatten();
}
function applyRepeatFlatten() {
  const template = props.model.actions[0] ?? new AutomationAction({ type: 'call_service', provider: provider.value, service: service.value });
  const parameters = template.parameters.map(p => ({
    key: p.key,
    ...(p.source != null ? { source: p.source } : {}),
    value: p.value,
  }));
  props.model.actions = Array.from({ length: repeatCount.value }, () =>
    new AutomationAction({
      type: 'call_service',
      provider: template.provider,
      service: template.service,
      parameters,
      target: template.target,
      data: template.data,
      editorMetadata: template.editorMetadata,
    })
  );
}
</script>
